import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../../../src/theme/getTheme'

export function getLandingPricingPackagesStyles(theme: Theme) {
  return StyleSheet.create({
  section: {
    paddingHorizontal: 24,
    paddingVertical: 52,
    backgroundColor: theme.background.shell,
  },
  sectionPhone: {
    paddingHorizontal: 16,
    paddingVertical: 38,
  },
  container: {
    maxWidth: 1240,
    marginHorizontal: 'auto' as unknown as number,
    width: '100%',
  },
  headerRow: {
    alignItems: 'center',
    gap: 24,
    marginBottom: 42,
  },
  headerRowPhone: {
    marginBottom: 28,
  },
  headerCopy: {
    maxWidth: 700,
    alignItems: 'center',
  },
  eyebrow: {
    fontFamily:
      Platform.OS === 'web' ? '"JetBrains Mono", ui-monospace, monospace' : undefined,
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.6,
    color: theme.primary,
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  h2: {
    fontFamily:
      Platform.OS === 'web'
        ? '"Space Grotesk", "Inter", system-ui, sans-serif'
        : undefined,
    fontSize: Platform.OS === 'web' ? 42 : 28,
    fontWeight: '700',
    lineHeight: Platform.OS === 'web' ? 48 : 34,
    letterSpacing: -1,
    color: theme.gray[900],
    marginBottom: 14,
    textAlign: 'center',
  },
  h2Phone: {
    fontSize: 30,
    lineHeight: 36,
    letterSpacing: -0.7,
  },
  p: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 17,
    lineHeight: 27,
    color: theme.gray[600],
    textAlign: 'center',
  },
  pPhone: {
    fontSize: 15,
    lineHeight: 23,
  },
  cards: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: 18,
    ...(Platform.OS === 'web'
      ? ({
          width: '100%' as never,
          alignItems: 'stretch',
        } as const)
      : {}),
  },
  cardsStacked: {
    flexDirection: 'column',
    flexWrap: 'nowrap' as unknown as 'wrap',
    maxWidth: 640,
    width: '100%',
    alignSelf: 'center',
    gap: 0,
    ...(Platform.OS === 'web'
      ? ({
          alignItems: 'stretch',
        } as const)
      : {}),
  },
  /** Desktop 3+2 grid: outer column container. */
  cardsGrid: {
    flexDirection: 'column',
    gap: 18,
    ...(Platform.OS === 'web' ? ({ width: '100%' as never } as const) : {}),
  },
  /** Top row: Hobby + Pro Monthly + Pro Yearly */
  cardsRow1: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: 18,
    ...(Platform.OS === 'web'
      ? ({ alignItems: 'stretch', width: '100%' as never } as const)
      : {}),
  },
  /** Bottom row: Business + Enterprise — wider cards, centered */
  cardsRow2: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: 18,
    ...(Platform.OS === 'web'
      ? ({
          alignItems: 'stretch',
          maxWidth: 840 as unknown as never,
          width: '100%' as never,
          alignSelf: 'center' as never,
        } as const)
      : {}),
  },
  card: {
    flex: Platform.OS === 'web' ? 1 : undefined,
    minHeight: 430,
    backgroundColor: theme.background.card,
    borderRadius: 24,
    padding: 28,
    borderWidth: 1,
    borderColor: theme.gray[300],
    shadowColor: theme.primaryDark,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.08,
    shadowRadius: 22,
    elevation: 4,
    /** In-card dropdown (Business tier) must not clip the open menu on web. */
    ...(Platform.OS === 'web' ? ({ overflow: 'visible' as const } as const) : {}),
  },
  cardStacked: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto' as unknown as number,
    minHeight: 0,
    marginBottom: 20,
    overflow: 'visible',
    ...(Platform.OS === 'web'
      ? ({
          width: '100%' as never,
          alignSelf: 'stretch',
          boxSizing: 'border-box',
          height: 'auto' as never,
        } as const)
      : {}),
  },
  cardStackedLast: {
    marginBottom: 0,
  },
  cardPhone: {
    borderRadius: 20,
    padding: 20,
  },
  /** Keep current-plan emphasis subtle on the dark pricing section. */
  cardYourPlan: {
    borderWidth: 2,
    borderColor: `${theme.primaryLight}CC`,
  },
  badgeYourPlan: {
    alignSelf: 'flex-start',
    backgroundColor: theme.primaryDark,
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 6,
    marginBottom: 14,
  },
  badgeYourPlanText: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  cardFeatured: {
    borderColor: theme.primaryLight,
    backgroundColor: theme.background.cardElevated,
    shadowOpacity: 0.16,
  },
  iconBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 18,
  },
  badgeSlot: {
    flex: 1,
    alignItems: 'flex-end',
    justifyContent: 'center',
    minHeight: 52,
  },
  badge: {
    alignSelf: 'flex-end',
    backgroundColor: theme.accent,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 999,
  },
  /** Dark text on yellow pill — purple on #F6E05E was below readable contrast. */
  badgeText: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 12,
    fontWeight: '800',
    color: theme.textOnCard,
  },
  iconWrap: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: theme.primary + '1A',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconWrapPhone: {
    width: 44,
    height: 44,
    borderRadius: 13,
  },
  iconWrapFeatured: {
    backgroundColor: theme.primary,
  },
  cardTitle: {
    fontFamily:
      Platform.OS === 'web'
        ? '"Space Grotesk", "Inter", system-ui, sans-serif'
        : undefined,
    fontSize: 22,
    fontWeight: '700',
    letterSpacing: -0.45,
    color: theme.gray[900],
    marginBottom: 8,
  },
  cardTitlePhone: {
    fontSize: 19,
    letterSpacing: -0.3,
    marginBottom: 6,
  },
  cardPrice: {
    fontFamily:
      Platform.OS === 'web'
        ? '"Space Grotesk", "Inter", system-ui, sans-serif'
        : undefined,
    fontSize: 28,
    fontWeight: '800',
    letterSpacing: -0.8,
    color: theme.primary,
    marginBottom: 12,
  },
  cardDescription: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 15,
    lineHeight: 24,
    color: theme.gray[600],
    marginBottom: 22,
  },
  cardDescriptionPhone: {
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 16,
  },
  bullets: {
    flex: 1,
    gap: 10,
  },
  /** Stacked cards: avoid flex-grow fighting layout on web (was collapsing/overlapping lists). */
  bulletsStacked: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto' as unknown as number,
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  checkWrap: {
    width: 20,
    paddingTop: 1,
    alignItems: 'center',
  },
  bulletText: {
    flex: 1,
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 14,
    lineHeight: 20,
    color: theme.gray[800],
  },
  bulletTextPhone: {
    fontSize: 13,
    lineHeight: 19,
  },
  cardCta: {
    marginTop: 24,
    minHeight: 46,
    borderRadius: 14,
    backgroundColor: theme.primary,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 18,
  },
  cardCtaCompany: {
    backgroundColor: theme.background.card,
    borderWidth: 2,
    borderColor: theme.primary,
  },
  cardCtaHovered: {
    opacity: 0.94,
  },
  cardCtaText: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 14,
    fontWeight: '800',
    color: '#FFFFFF',
  },
  cardCtaCompanyText: {
    color: theme.primary,
  },
  cardCtaDisabled: {
    opacity: 0.55,
  },
  orgPlanDisclosure: {
    fontSize: 12,
    lineHeight: 18,
    color: theme.gray[500],
  },
  orgPlanDisclosurePhone: {
    textAlign: 'left',
  },
  footnote: {
    marginTop: 34,
    maxWidth: 680,
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 13,
    lineHeight: 20,
    color: theme.gray[500],
  },
  footnotePhone: {
    textAlign: 'center',
    alignSelf: 'center',
  },
  })
}
